import { defineStore } from 'pinia'

interface FlashMessage {
  text: string
  type?: string
  timer?: ReturnType<typeof setTimeout>
  id: number
  expiration: number
}

interface FlashMessengerState {
  queue: FlashMessage[]
  currentMessage: FlashMessage | null
  isProcessing: boolean
}

const UI_TRANSITION_DELAY = 500

export const useFlashMessengerStore = defineStore('flashMessenger', {
  state: (): FlashMessengerState => ({
    queue: [],
    currentMessage: null,
    isProcessing: false
  }),

  getters: {
    message: (state): FlashMessage | null => state.currentMessage,
    hasMessages: (state): boolean =>
      state.queue.length > 0 || state.currentMessage !== null
  },

  actions: {
    clearMessage(): void {
      if (this.currentMessage?.timer) {
        clearTimeout(this.currentMessage.timer)
      }
      this.currentMessage = null
      this.isProcessing = false
      setTimeout(() => {
        this.processNextMessage()
      }, UI_TRANSITION_DELAY)
    },

    addMessage(
      payload: { expiration: number } & Omit<FlashMessage, 'timer' | 'id'>
    ): void {
      const message: FlashMessage = {
        ...payload,
        id: Math.random()
      }

      this.queue.push(message)

      if (!this.isProcessing) {
        this.processNextMessage()
      }
    },
    removeMessage(): void {
      this.clearMessage()
    },
    processNextMessage(): void {
      if (this.queue.length === 0 || this.isProcessing) {
        return
      }

      this.isProcessing = true
      const message = this.queue.shift()!

      const messageWithTimer: FlashMessage = {
        ...message,
        timer: setTimeout(this.clearMessage, message.expiration)
      }

      this.currentMessage = messageWithTimer
    }
  }
})
